import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Bell, Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { cn, formatDate } from '@/lib/utils';
import { Badge, Button } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AlertTask {
  id: number;
  displayId: string;
  title: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  status: string;
  isNew: boolean;
}
interface Alerts {
  counts: { overdue: number; dueToday: number; dueThisWeek: number; newSinceSeen: number };
  items: AlertTask[];
}
interface MyMonth {
  overdueOpen: number;
  streakDays: number;
}
interface FeedItem {
  id: number;
  kind: string;
  title: string;
  href: string | null;
  readAt: string | null;
  createdAt: string;
}

const PRIORITY_TONE: Record<string, 'red' | 'amber' | 'muted'> = {
  high: 'red',
  medium: 'amber',
  low: 'muted',
};

function Section({
  title,
  tone,
  items,
  onComplete,
  completingId,
  isNew,
}: {
  title: string;
  tone: string;
  items: AlertTask[];
  onComplete: (task: AlertTask) => void;
  completingId: number | null;
  isNew: (task: AlertTask) => boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div className="border-b border-border last:border-0">
      <div className={cn('px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide', tone)}>
        {title} · {items.length}
      </div>
      <ul>
        {items.map((task) => (
          <li key={task.id} className="group/row flex items-start gap-1 pr-2 hover:bg-accent">
            <Link
              href={`/tasks?q=${encodeURIComponent(task.displayId)}`}
              className="flex min-w-0 flex-1 items-start justify-between gap-2 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {isNew(task) && (
                    <span
                      className="size-1.5 shrink-0 rounded-full bg-primary"
                      title="New since you last looked"
                      aria-label="New"
                    />
                  )}
                  <span className="truncate text-[13px] font-medium">{task.title}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-mono">{task.displayId}</span> · due {formatDate(task.dueDate)}
                </div>
              </div>
              <Badge tone={PRIORITY_TONE[task.priority] ?? 'muted'}>{task.priority}</Badge>
            </Link>
            <button
              type="button"
              disabled={completingId === task.id}
              onClick={() => onComplete(task)}
              aria-label={`Mark ${task.displayId} done`}
              title="Mark done"
              className="mt-2 flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-success/40 hover:bg-success/10 hover:text-success disabled:opacity-50"
            >
              <Check className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Header alert bell — the signed-in user's own tasks only (the endpoint takes
 * no assignee parameter). The badge counts what needs action now (overdue +
 * due today); the panel also shows the rest of the week. Keyed under ['tasks']
 * so any task mutation elsewhere refreshes it via prefix invalidation.
 */
export function AlertBell() {
  const qc = useQueryClient();
  const [completingId, setCompletingId] = useState<number | null>(null);
  // Opening stamps "seen", which clears isNew on the next refetch — so the
  // rows that were new at open time are frozen here for as long as the panel
  // stays open. Otherwise the markers vanish before they can be read.
  const [newAtOpen, setNewAtOpen] = useState<Set<number>>(new Set());
  const { data } = useQuery({
    queryKey: ['tasks', 'mine', 'alerts'],
    queryFn: () => api.get<Alerts>('/api/tasks/mine/alerts'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // Same personal stats as the dashboard's "My month" card (shared cache key).
  const { data: myMonth } = useQuery({
    queryKey: ['tasks', 'mine', 'month'],
    queryFn: () => api.get<MyMonth>('/api/tasks/mine/month'),
    staleTime: 60_000,
  });
  // Personal event feed (task assigned / comments / status changes).
  const { data: feed } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: FeedItem[]; unread: number }>('/api/notifications'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const markRead = useMutation({
    mutationFn: (nid: number) => api.patch(`/api/notifications/${nid}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Stamping on open clears the dot; the panel keeps showing this render's
  // per-row markers so you can still see what was new while you read it.
  const markSeen = useMutation({
    mutationFn: () => api.post('/api/tasks/mine/alerts/seen'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks', 'mine', 'alerts'] }),
  });

  const complete = useMutation({
    mutationFn: (task: AlertTask) =>
      api.patch(`/api/tasks/${task.id}/status`, { status: 'completed' }),
    onMutate: (task: AlertTask) => setCompletingId(task.id),
    onSuccess: (_r, task) => {
      toast.success(`${task.displayId} marked done.`);
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['nav', 'badges'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update the task'),
    onSettled: () => setCompletingId(null),
  });

  const counts = data?.counts;
  const urgent = (counts?.overdue ?? 0) + (counts?.dueToday ?? 0);
  const unreadFeed = feed?.unread ?? 0;
  const hasNew = (counts?.newSinceSeen ?? 0) > 0 || unreadFeed > 0;
  const items = data?.items ?? [];
  const shownNew = items.filter((i) => i.isNew || newAtOpen.has(i.id)).length;
  const today = new Date().toLocaleDateString('en-CA');
  const overdue = items.filter((i) => i.dueDate < today);
  const dueToday = items.filter((i) => i.dueDate === today);
  const dueWeek = items.filter((i) => i.dueDate > today);

  const base = urgent > 0 ? `Alerts: ${urgent} task(s) need attention` : 'Alerts: nothing urgent';
  const label = hasNew ? `${base}, ${counts!.newSinceSeen} new` : base;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          setNewAtOpen(new Set(items.filter((i) => i.isNew).map((i) => i.id)));
          if (hasNew) markSeen.mutate();
        } else {
          setNewAtOpen(new Set());
        }
      }}
    >
      <DropdownMenuTrigger
        render={<Button variant="ghost" size="icon-sm" aria-label={label} className="relative" />}
      >
        <Bell className="size-[18px]" />
        {urgent > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground"
          >
            {urgent > 9 ? '9+' : urgent}
          </span>
        )}
        {/* Nothing urgent, but something arrived since you last looked. */}
        {urgent === 0 && hasNew && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary ring-2 ring-background"
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            My alerts
            {shownNew > 0 && <Badge tone="blue">{shownNew} new</Badge>}
          </span>
          <span className="text-xs text-muted-foreground">
            {counts ? `${counts.overdue} overdue · ${counts.dueToday} today` : '—'}
          </span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {(feed?.items.length ?? 0) > 0 && (
            <div className="border-b border-border">
              <div className="flex items-center justify-between px-3 py-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Notifications{unreadFeed > 0 ? ` · ${unreadFeed} new` : ''}
                </span>
                {unreadFeed > 0 && (
                  <button
                    type="button"
                    className="text-[11px] text-primary hover:underline"
                    onClick={() => markAllRead.mutate()}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <ul>
                {feed!.items.slice(0, 8).map((f) => (
                  <li key={f.id} className="hover:bg-accent">
                    <Link
                      href={f.href ?? '/tasks'}
                      className="flex items-start gap-2 px-3 py-1.5"
                      onClick={() => {
                        if (!f.readAt) markRead.mutate(f.id);
                      }}
                    >
                      {!f.readAt && (
                        <span aria-hidden="true" className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                      )}
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-xs',
                          f.readAt ? 'text-muted-foreground' : 'font-medium',
                        )}
                        title={f.title}
                      >
                        {f.title}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {formatDate(f.createdAt)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              You&apos;re all caught up.
            </p>
          ) : (
            <>
              {(
                [
                  ['Overdue', 'text-destructive', overdue],
                  ['Due today', 'text-warning', dueToday],
                  ['Due this week', 'text-muted-foreground', dueWeek],
                ] as const
              ).map(([title, tone, list]) => (
                <Section
                  key={title}
                  title={title}
                  tone={tone}
                  items={list}
                  onComplete={(task) => complete.mutate(task)}
                  completingId={completingId}
                  isNew={(task) => task.isNew || newAtOpen.has(task.id)}
                />
              ))}
            </>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <Link href="/tasks" className="text-xs text-primary hover:underline">
            View all my tasks
          </Link>
          {myMonth && (
            <span className="text-xs text-muted-foreground">
              {myMonth.overdueOpen === 0 ? (
                <>
                  <span className="font-semibold text-success">{myMonth.streakDays}</span>
                  {myMonth.streakDays === 1 ? ' day' : ' days'} zero-overdue streak
                </>
              ) : (
                'Clear overdue to start a streak'
              )}
            </span>
          )}
        </div>
      </DropdownMenuContent>
      <span aria-live="polite" className="sr-only">
        {urgent > 0 ? `${urgent} tasks need attention` : ''}
      </span>
    </DropdownMenu>
  );
}
